// Sign-up resume handler — landing zone after Stripe's bank-redirect
// 3DS round-trip (set as `return_url` in `confirmPayment`). Reads
// `redirect_status` from the URL and either sends the user to the
// login page (success) or back to the step where they left off
// (failure).
//
// On failure, also tells the backend to release the pending signup
// (`is_deleted=True`, cancel the Stripe subscription + PaymentIntent)
// so the email is free for a retry.
//
// Only the bank-redirect 3DS path lands here; cards that don't require
// 3DS — and 3DS that completes via the inline iframe modal — never
// leave the SPA, so this component isn't reached for them.
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';

import * as actions from '../../../redux/actions/index';
import * as api from '../../../api/SignUp';
import {
  readSignupResumeState,
  clearSignupResumeState,
} from '../../../utils/signupResume';

const stepCompletedFor = (stepPath) => {
  if (stepPath.startsWith('/sign-up/step-5')) return 4;
  if (stepPath.startsWith('/sign-up/step-2')) return 1;
  return 0;
};

const SignUpResume = ({ onUpdateSignUpData, onSetStepCompleted }) => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirectStatus = searchParams.get('redirect_status');

    if (redirectStatus === 'succeeded') {
      clearSignupResumeState();
      history.replace('/login/?status=registered');
      return;
    }

    // 3DS failed or was abandoned. Fire-and-forget the abort call so the
    // email becomes free for a retry. `payment_intent_client_secret` is
    // appended to the return URL by Stripe and authorizes the abort on
    // the backend (it's the unique key on the PaymentIntent row).
    const clientSecret = searchParams.get('payment_intent_client_secret');
    if (clientSecret) {
      api.signUpAbort(clientSecret).catch(() => {});
    }

    const saved = readSignupResumeState();
    clearSignupResumeState();

    if (!saved || !saved.stepPath) {
      history.replace('/sign-up/step-1');
      return;
    }

    if (saved.data) {
      onUpdateSignUpData(saved.data);
    }
    onSetStepCompleted(stepCompletedFor(saved.stepPath));

    const separator = saved.stepPath.includes('?') ? '&' : '?';
    history.replace(`${saved.stepPath}${separator}payment_failed=1`);
    // eslint-disable-next-line
  }, []);

  return null;
};

const mapDispatchToProps = (dispatch) => ({
  onUpdateSignUpData: (data) => dispatch(actions.updateSignUpData(data)),
  onSetStepCompleted: (stepNum) => dispatch(actions.setStepCompleted(stepNum)),
});

export default connect(null, mapDispatchToProps)(SignUpResume);
